import Decimal from 'decimal.js'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'

type CustomTarget = { name?: string; value: string, floatValue?: number }
type CustomProps = Omit<NumericFormatProps, 'onChange'> & {
  value?: number
  onChange?: (event: { target: CustomTarget }) => void
  name?: string
  maxLimit?: number
  debounceMilliseconds?: number
}
export type HTMLCustomNumericElement = HTMLInputElement & {
  name?: string
  value: string
  floatValue?: number
}

const DEFAULT_MAX_LIMIT = 1e10 // 10,000,000,000

const CustomNumericFormat = forwardRef<HTMLCustomNumericElement, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, maxLimit, value, debounceMilliseconds, ...other } = props
    const [inputValue, setInputValue] = useState('')
    const [focused, setFocused] = useState(false)
    const timeoutId = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      if (!focused && (value || inputValue)) {
        setInputValue(value != null ? new Decimal(Number(value)).toFixed() : '')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focused, value])

    return (
      <NumericFormat
        {...other}
        value={inputValue}
        getInputRef={ref}
        onValueChange={({ floatValue, value }, { event = {} }) => {
          setInputValue(value)
          const params = {
            ...event,
            target: {
              ...event.target,
              name: props.name,
              floatValue,
              value,
            },
          }
          if (debounceMilliseconds) {
            timeoutId.current && clearTimeout(timeoutId.current)
            timeoutId.current = setTimeout(() => {
              onChange?.(params)
            }, debounceMilliseconds)
          } else {
            onChange?.(params)
          }
        }}
        thousandSeparator
        valueIsNumericString
        allowedDecimalSeparators={[',']}
        isAllowed={(values) => {
          const floatValue = values.floatValue || 0
          return floatValue <= (maxLimit || DEFAULT_MAX_LIMIT)
        }}
        onBlur={(event) => {
          setFocused(false)
          other.onBlur?.(event)
        }}
        onFocus={(event) => {
          setFocused(true)
          other.onFocus?.(event)
        }}
        inputMode="decimal"
      />
    )
  },
)

export default CustomNumericFormat
